import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';

/**
 * ComposerSheet — Reusable full-screen / bottom-sheet overlay for mobile-first interactions.
 *
 * Props:
 *   @param {boolean}  open        - Whether the sheet is visible
 *   @param {function} onClose     - Called when user dismisses (X, backdrop, Escape)
 *   @param {string}   title       - Header title text
 *   @param {string}   [subtitle]  - Optional description below title
 *   @param {ReactNode} [icon]     - Optional icon element next to title
 *   @param {ReactNode} children   - Scrollable body content
 *   @param {ReactNode} [footer]   - Sticky bottom action area (buttons)
 *   @param {string}   [maxWidth]  - Desktop max-width class (default: "sm:max-w-lg")
 *   @param {string}   [testId]    - data-testid for the root element
 */
export default function ComposerSheet({ open, onClose, title, subtitle, icon, children, footer, maxWidth = 'sm:max-w-lg', testId }) {
  const containerRef = useRef(null);

  // Lock body scroll + prevent iOS bounce
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    const prevPos = document.body.style.position;
    const prevTop = document.body.style.top;
    const scrollY = window.scrollY;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = prev;
      document.body.style.position = prevPos;
      document.body.style.top = prevTop;
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Track visual viewport (keyboard open/close) — apply to CONTAINER
  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    const sync = () => {
      const el = containerRef.current;
      if (!el) return;
      el.style.height = `${vv.height}px`;
      el.style.top = `${vv.offsetTop}px`;
      el.style.left = '0';
      el.style.right = '0';
      el.style.bottom = 'auto';
    };

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      // Reset inline styles when closing
      const el = containerRef.current;
      if (el) {
        el.style.height = '';
        el.style.top = '';
        el.style.bottom = '';
      }
    };
  }, [open]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
      onClick={handleBackdropClick}
      data-testid={testId}
      style={{ willChange: 'height, top' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" onClick={onClose} />

      {/* Sheet — fills container on mobile, auto-height on tablet+ */}
      <div
        className={`relative flex flex-col bg-white w-full ${maxWidth}
          rounded-t-2xl sm:rounded-2xl md:rounded-2xl
          shadow-2xl
          h-full sm:h-auto sm:max-h-[90vh]`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-start justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b border-gray-100 shrink-0">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 leading-tight truncate">{title}</h2>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 -mr-1.5 -mt-0.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            data-testid={testId ? `${testId}-close` : 'composer-sheet-close'}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── Scrollable Body ── */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-5 py-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {children}
        </div>

        {/* ── Sticky Footer ── */}
        {footer && (
          <div
            className="shrink-0 px-4 sm:px-5 py-3 border-t border-gray-100 bg-white rounded-b-none sm:rounded-b-2xl"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
